import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { API_ORIGIN } from '../constants'
import { authorizedFetch, parseApiErrorMessage } from '@/lib/api'

export interface OrganisationLocation {
  id: string
  city: string
  country: string
  description: string
}

export interface OrganisationLocationWrite {
  city: string
  country: string
  description: string
}

export interface Organisation {
  id: string
  name: string
  slug: string
  description: string
  website: string
  industry: string
  company_size: string
  founded_year: number | null
  city: string
  country: string
  locations?: OrganisationLocation[]
  linkedin_url: string
  x_url: string
  instagram_url: string
  facebook_url: string
  threads_url: string
  tiktok_url: string
  youtube_url: string
  substack_url: string
  github_url: string
  extra_social_order: string[]
  logo_url?: string | null
  banner_url?: string | null
  created_at: string
  updated_at: string
}

export interface CreateOrganisationData {
  name: string
  description?: string
  website?: string
  industry?: string
  company_size?: string
  founded_year?: number | null
  city?: string
  country?: string
  locations?: OrganisationLocationWrite[]
  linkedin_url?: string
  x_url?: string
  instagram_url?: string
  facebook_url?: string
  threads_url?: string
  tiktok_url?: string
  youtube_url?: string
  substack_url?: string
  github_url?: string
  extra_social_order?: string[]
}

export interface UpdateOrganisationData {
  name?: string
  slug?: string
  description?: string
  website?: string
  industry?: string
  company_size?: string
  founded_year?: number | null
  city?: string
  country?: string
  locations?: OrganisationLocationWrite[]
  linkedin_url?: string
  x_url?: string
  instagram_url?: string
  facebook_url?: string
  threads_url?: string
  tiktok_url?: string
  youtube_url?: string
  substack_url?: string
  github_url?: string
  extra_social_order?: string[]
}

const EMPTY_ORGANISATIONS: Organisation[] = []

async function organisationApiError(response: Response, fallback: string): Promise<never> {
  throw new Error(await parseApiErrorMessage(response, fallback))
}

/** Single source of truth for employer org detail (`['organisation', slug]`). Canvas + profile page subscribe here. */
export function useManagedOrganisationDetail(slug: string | undefined) {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['organisation', slug],
    queryFn: async (): Promise<Organisation> => {
      if (!slug) {
        throw new Error('Organisation slug missing')
      }
      const response = await authorizedFetch(`${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug)}/`)
      if (!response.ok) {
        throw new Error('Failed to fetch organisation')
      }
      return response.json()
    },
    enabled: !!slug && !!token,
  })
}

export function useOrganisations(options?: { managedListEnabled?: boolean }) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const fetchManaged = options?.managedListEnabled !== false

  const organisationsQuery = useQuery({
    queryKey: ['managed-organisations'],
    queryFn: async (): Promise<Organisation[]> => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/organisations/managed`)
      if (!response.ok) {
        throw new Error('Failed to fetch organisations')
      }
      return response.json()
    },
    enabled: !!token && fetchManaged,
  })

  const createOrganisationMutation = useMutation({
    mutationFn: async (data: CreateOrganisationData): Promise<Organisation> => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/organisations/managed`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        await organisationApiError(response, 'Failed to create organisation')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managed-organisations'] })
    },
  })

  const updateOrganisationMutation = useMutation({
    mutationFn: async ({ slug, data }: { slug: string; data: UpdateOrganisationData }): Promise<Organisation> => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug)}/`,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        },
      )
      if (!response.ok) {
        await organisationApiError(response, 'Failed to update organisation')
      }
      return response.json()
    },
    onSuccess: (data, variables) => {
      if (data.slug !== variables.slug) {
        queryClient.removeQueries({ queryKey: ['organisation', variables.slug] })
        queryClient.setQueryData(['organisation', data.slug], data)
      } else {
        queryClient.setQueryData(['organisation', variables.slug], data)
      }
      queryClient.invalidateQueries({ queryKey: ['managed-organisations'] })
    },
  })

  const deleteOrganisationMutation = useMutation({
    mutationFn: async (slug: string): Promise<void> => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug)}/`,
        {
          method: 'DELETE',
        },
      )
      if (!response.ok) {
        await organisationApiError(response, 'Failed to delete organisation')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managed-organisations'] })
    },
  })

  const uploadOrganisationLogoMutation = useMutation({
    mutationFn: async ({ slug, file }: { slug: string; file: File }): Promise<Organisation> => {
      const formData = new FormData()
      formData.append('file', file)
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug)}/logo/`,
        {
          method: 'POST',
          body: formData,
        }
      )
      if (!response.ok) {
        await organisationApiError(response, 'Upload failed')
      }
      return response.json()
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['organisation', variables.slug], data)
      queryClient.invalidateQueries({ queryKey: ['managed-organisations'] })
    },
  })

  const deleteOrganisationLogoMutation = useMutation({
    mutationFn: async (slug: string): Promise<Organisation> => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug)}/logo/`,
        { method: 'DELETE' }
      )
      if (!response.ok) {
        await organisationApiError(response, 'Could not remove logo')
      }
      return response.json()
    },
    onSuccess: (data, slug) => {
      queryClient.setQueryData(['organisation', slug], data)
      queryClient.invalidateQueries({ queryKey: ['managed-organisations'] })
    },
  })

  const uploadOrganisationBannerMutation = useMutation({
    mutationFn: async ({ slug, file }: { slug: string; file: File }): Promise<Organisation> => {
      const formData = new FormData()
      formData.append('file', file)
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug)}/banner/`,
        {
          method: 'POST',
          body: formData,
        }
      )
      if (!response.ok) {
        await organisationApiError(response, 'Upload failed')
      }
      return response.json()
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['organisation', variables.slug], data)
      queryClient.invalidateQueries({ queryKey: ['managed-organisations'] })
    },
  })

  const deleteOrganisationBannerMutation = useMutation({
    mutationFn: async (slug: string): Promise<Organisation> => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug)}/banner/`,
        { method: 'DELETE' }
      )
      if (!response.ok) {
        await organisationApiError(response, 'Could not remove cover')
      }
      return response.json()
    },
    onSuccess: (data, slug) => {
      queryClient.setQueryData(['organisation', slug], data)
      queryClient.invalidateQueries({ queryKey: ['managed-organisations'] })
    },
  })

  return {
    organisations: organisationsQuery.data ?? EMPTY_ORGANISATIONS,
    isLoading: organisationsQuery.isLoading,
    error: organisationsQuery.error,
    createOrganisation: createOrganisationMutation.mutate,
    isCreating: createOrganisationMutation.isPending,
    createError: createOrganisationMutation.error,
    updateOrganisation: updateOrganisationMutation.mutate,
    isUpdating: updateOrganisationMutation.isPending,
    updateError: updateOrganisationMutation.error,
    deleteOrganisation: deleteOrganisationMutation.mutate,
    isDeleting: deleteOrganisationMutation.isPending,
    deleteError: deleteOrganisationMutation.error,
    uploadOrganisationLogo: uploadOrganisationLogoMutation.mutateAsync,
    isUploadingOrganisationLogo: uploadOrganisationLogoMutation.isPending,
    deleteOrganisationLogo: deleteOrganisationLogoMutation.mutateAsync,
    isDeletingOrganisationLogo: deleteOrganisationLogoMutation.isPending,
    uploadOrganisationBanner: uploadOrganisationBannerMutation.mutateAsync,
    isUploadingOrganisationBanner: uploadOrganisationBannerMutation.isPending,
    deleteOrganisationBanner: deleteOrganisationBannerMutation.mutateAsync,
    isDeletingOrganisationBanner: deleteOrganisationBannerMutation.isPending,
  }
}
